import { EmailFieldUI } from '@axo/form/feature/form-input/EmailField/EmailFieldUI';
import { LoanDurationField } from '@axo/form/feature/form-input/LoanDurationField';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { PhoneFieldUI } from '@axo/form/feature/form-input/PhoneField/PhoneFieldUI';
import { RadioFieldUI } from '@axo/form/feature/form-input/RadioField/RadioFieldUI';
import SliderFieldUI from '@axo/form/feature/form-input/SliderField/SliderFieldUI';
import { useTranslation } from '@axo/shared/i18n';
import { calculateMonthlyPayment } from '@axo/shared/util/loan';
import { Button } from '@axo/ui-core/components/Button';
import { Message } from '@axo/ui-core/components/Message';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Form } from '../../../../../components/Form';
import { Navigation } from '../../../../../components/Navigation';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import useInitiatedTracking from '../../../../../hooks/tracking/useInitiatedTracking';
import {
  getMarketingConsentDefaultValues,
  useMarketingConsent,
} from '../../../../../hooks/useMarketingConsent';
import { formatNumber, formatSpacedNumber } from '../../../../../utils/format';
import { useEmailValidation } from '../../../../../utils/useEmailValidation';
import { verifyThreshold } from '../../../../../utils/verifyThreshold';
import {
  DEFAULT_LOAN_AMOUNT,
  DEFAULT_LOAN_DURATION,
  initialPurposeOptions,
  MAX_LOAN_AMOUNT,
  MIN_LOAN_AMOUNT,
  RATES,
} from '../../config/globals';
import { Disclaimer } from '../../Disclaimer';
import { PurposeFormGroupData, Step1Data } from '../../FormData.dk.types';
import { ConsentSection } from '../../Main/ConsentSection';
import {
  getEmailRegisterOptions,
  phoneFieldOptions,
  socialSecurityNumberFieldOptions,
} from '../shared-config';
import {
  appliedAmountFieldOptions,
  initialPurposeFieldOptions,
} from './config';
import { DuplicatedApplicationModal } from './DuplicatedApplicationModal';
import { PurposeFormGroup } from './PurposeFormGroup';
import { useDuplicateApplication } from './useDuplicateApplication';

const touchedFieldsOverride: Partial<keyof Step1Data>[] = [
  'LoanApplication/AppliedAmount',
  'LoanApplication/LoanDuration',
  'LoanApplication/InitialPurpose',
];

const getDefaultValues = ({
  loanAmount,
  loanDuration,
  marketingConsent,
  showMarketingConsent,
}: {
  loanAmount: number | undefined;
  loanDuration: number | undefined;
  marketingConsent?: boolean;
  showMarketingConsent?: boolean;
}): Step1Data => ({
  'Frontend/IsRefinancing': null,
  'LoanApplication/InitialPurpose': null,
  'LoanApplication/AppliedAmount': loanAmount || DEFAULT_LOAN_AMOUNT,
  'LoanApplication/LoanDuration': loanDuration || DEFAULT_LOAN_DURATION,
  'Customer/Email': '',
  'Customer/MobilePhoneNumber': '',
  'Person/SocialSecurityNumber': '',
  ...getMarketingConsentDefaultValues({
    marketingConsent,
    showMarketingConsent,
    defaultValueOverride: false,
  }),
});

interface Props {
  isPre?: boolean;
  heading?: string;
  options?: {
    defaultLoanAmount?: number;
    defaultLoanDuration?: number;
  };
}

export const Step1 = ({ isPre, options }: Props) => {
  const { t } = useTranslation();

  const [showMore, setShowMore] = useState(false);

  const { baseUrl, legacyUrl, onChange, onSubmit, onPreSubmit, axoFormData } =
    useAxoFormContext<Step1Data>();

  const { validate, isModalOpen, setIsModalOpen } =
    useDuplicateApplication(legacyUrl);

  const form = useForm({
    defaultValues: getDefaultValues({
      loanAmount:
        axoFormData['LoanApplication/AppliedAmount'] ??
        options?.defaultLoanAmount,
      loanDuration:
        axoFormData['LoanApplication/LoanDuration'] ??
        options?.defaultLoanDuration,
      marketingConsent: axoFormData['Ignore/MarketingConsent'],
      showMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
    }),
    shouldUnregister: true,
    mode: 'onChange',
  });

  const {
    watch,
    setValue,
    setError,
    formState: { isSubmitting, isSubmitSuccessful, touchedFields },
  } = form;

  const loading = isSubmitting || isSubmitSuccessful;

  const showMoreFields = showMore || axoFormData['Customer/Email'];

  const { showMarketingConsent = true } = useMarketingConsent({
    marketingConsent: axoFormData['Ignore/MarketingConsent'],
    shouldShowMarketingConsent: axoFormData['Ignore/ShowMarketingConsent'],
  });

  useInitiatedTracking({
    shouldTriggerOnInitiated: Object.keys(touchedFields).length > 0,
  });

  const { EmailPrompt, isValid, formError, enableEmailValidation } =
    useEmailValidation({
      baseUrl,
      email: watch('Customer/Email'),
      setEmail: (email) => setValue('Customer/Email', email),
    });

  const emailRegisterOptions = getEmailRegisterOptions(enableEmailValidation);

  const appliedAmount = axoFormData['LoanApplication/AppliedAmount'] ?? 0;
  const loanDuration = axoFormData['LoanApplication/LoanDuration'] ?? 0;

  const displayRecommended = verifyThreshold([
    { limit: 50_000, duration: 5 },
    { limit: 100_000, duration: 7 },
    { limit: 200_000, duration: 9 },
    { limit: 300_000, duration: 12 },
    { limit: 400_000, duration: 15 },
  ])({
    amount: appliedAmount,
    duration: loanDuration,
  });

  const _onSubmit = async (data: Step1Data) => {
    if (!data['Person/SocialSecurityNumber'])
      throw new Error('Missing Social Security Number');

    const duplicatedError = await validate(data['Person/SocialSecurityNumber']);

    if (duplicatedError) {
      form.setError('Person/SocialSecurityNumber', {
        message: duplicatedError,
      });
      return;
    }

    if (!isValid) {
      setError('Customer/Email', { message: 'Email.error.pattern' });
      return;
    }

    if (isPre) {
      onPreSubmit(data);
      return;
    }

    onSubmit(data);
  };

  return (
    <>
      <DuplicatedApplicationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <FormProvider {...form}>
        <Form
          onSubmit={_onSubmit}
          onChange={onChange}
          /* We need to override the touched attribute for this for it to trigger a change immediately when it is interacted with.
         Otherwise, the user would need to interact with another field before the change is triggered.*/
          touchedFieldsOverride={touchedFieldsOverride}
        >
          <NumberFieldUI<Step1Data>
            name="LoanApplication/AppliedAmount"
            label={t('AppliedAmount.label')}
            data-testid="applied-amount-input"
            autoSetValues
            size="large"
            suffix={t('suffix.currency')}
            format={formatSpacedNumber}
            disableSuccessState={true}
            {...appliedAmountFieldOptions}
          />

          <SliderFieldUI<Step1Data>
            name="LoanApplication/AppliedAmount"
            min={MIN_LOAN_AMOUNT}
            max={MAX_LOAN_AMOUNT}
            step={5_000}
            valueSuffix={t('suffix.currency')}
          />

          <LoanDurationField<Step1Data>
            name="LoanApplication/LoanDuration"
            country="dk"
            maxDuration={15}
            minDuration={1}
            recommended={displayRecommended}
            loanAmountPerMonth={calculateMonthlyPayment({
              loanAmount: appliedAmount,
              loanDuration,
              minRate: RATES.MIN,
            })}
          />

          {!displayRecommended && (
            <Message hasIcon variant={'warn'} size={'s'}>
              {t('DurationWarning')}
            </Message>
          )}

          <RadioFieldUI<PurposeFormGroupData>
            name="Frontend/IsRefinancing"
            label={t('InitialPurpose.label')}
            data-testid="refinancing-radio"
            radioStyle="both"
            options={initialPurposeOptions}
            {...initialPurposeFieldOptions}
          />

          {showMoreFields ? (
            <>
              <PurposeFormGroup />

              <EmailFieldUI<Step1Data>
                name="Customer/Email"
                label={t('Email.label')}
                data-testid="email-input"
                registerOptions={emailRegisterOptions}
                error={formError}
                confirmationPrompt={<EmailPrompt />}
              />

              <PhoneFieldUI<Step1Data>
                name="Customer/MobilePhoneNumber"
                label={t('MobilePhoneNumber.label')}
                data-testid="phone-number-input"
                format={formatNumber}
                {...phoneFieldOptions}
              />

              <NumberFieldUI<Step1Data>
                name="Person/SocialSecurityNumber"
                label={t('SocialSecurityNumber.label')}
                data-testid="ssn-input"
                placeholder={t('SocialSecurityNumber.placeholder')}
                format="******-****"
                {...socialSecurityNumberFieldOptions}
              />

              <ConsentSection showMarketingConsent={showMarketingConsent} />

              <Navigation.Continue loading={loading}>
                {t('next')}
              </Navigation.Continue>
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              size="large"
              data-testid="button-show-more"
              onClick={() => setShowMore(true)}
            >
              {t('next')}
            </Button>
          )}

          <Disclaimer
            appliedAmount={appliedAmount}
            loanDuration={loanDuration}
            rates={RATES}
          />
        </Form>
      </FormProvider>
    </>
  );
};
